// Üks parkikaart: päis (nimi + host pill + online indikaator), flow rida (Tootmine,
// [Aku], Tarbimine, Võrku/Võrgust). Klõpsamisel laieneb detailipaneeliks
// (mõõdikud + akusüsteem). Port mallist parks_dashboard.py renderParks().

import { Link } from 'react-router-dom';
import { fmt, pcsStatusInfo, fmtAge, isDeviceOnline } from '../../utils/format';

// Age-tag stiil — punane "OFFLINE" kui andmed vanad
function ageStyle(device) {
  const offline = device && !isDeviceOnline(device);
  return {
    marginLeft: 8,
    fontSize: '0.85em',
    fontWeight: 'normal',
    color: offline ? '#d33' : 'inherit',
    opacity: offline ? 1 : 0.6,
  };
}
function ageLabel(device) {
  return isDeviceOnline(device) ? fmtAge(device) : `OFFLINE · ${fmtAge(device)}`;
}

export function ParkCard({ park }) {
  // Disabled park — kuva ainult nimi + sõnum (nt "Markedroidi integreerimisel")
  if (park.disabled) {
    return (
      <div className="park placeholder">
        <div className="park-head">
          <h2>{park.name}</h2>
          <div className="meta">
            <span className="host">{park.host}</span>
            <span className="pill pill-mute">
              <span className="dot" />
              Integreerimisel
            </span>
          </div>
        </div>
        <div className="offline-msg">{park.display_message || 'Integreerimisel'}</div>
      </div>
    );
  }

  // Placeholder — paigaldamata park (name === null)
  if (!park.name) {
    return (
      <div className="park placeholder">
        <div className="park-head">
          <h2>Park #{park.slot}</h2>
          <div className="meta">
            <span className="host">{park.host}</span>
            <span className="pill pill-mute">
              <span className="dot" />
              Pole paigaldatud
            </span>
          </div>
        </div>
        <div className="offline-msg">Pole paigaldatud</div>
      </div>
    );
  }

  // Offline park
  if (!park.online) {
    return (
      <div className="park offline">
        <div className="park-head">
          <h2>{park.name}</h2>
          <div className="meta">
            <span className="host">{park.host}</span>
            <span className="pill pill-off">
              <span className="dot" />
              Offline
            </span>
          </div>
        </div>
        <div className="offline-msg">
          <svg style={{ color: '#dc2626' }}>
            <use href="#i-warn" />
          </svg>
          Gateway ei vasta
        </div>
      </div>
    );
  }

  // Online park
  const pSolar = park.solar?.p_total;
  const pGrid = park.grid?.p_total;
  const cons = park.consumption;
  const exporting = pGrid !== null && pGrid !== undefined && pGrid < 0;
  const isMuted = park.muted === true;

  // Aku andmed: kuvame kui kas PCS või BAU on online (BAU = autoriteetne SOC/temp/ohutus)
  const pcsAvail = !!(park.pcs_online && park.pcs);
  const bauAvail = !!(park.bau_online && park.bau);
  const hasBattery = pcsAvail || bauAvail;
  let batteryDetail = null;

  // Täiendavad akud (battery #2+) — dashboard lisarida iga aku kohta (SOC/temp/ohutus/alarmid)
  const extraBatteries = (park.batteries || []).filter((b) => b.bau).map((b) => {
    const bpcs = b.pcs || null;
    const bfault = b.bau?.fault_state || 0;
    let bc = 0, bn = b.bau?.alarm_level3 || 0; while (bn) { bc += bn & 1; bn >>>= 1; }
    return {
      battery: b.battery,
      soc: (b.bau?.soc != null) ? b.bau.soc : (bpcs?.bms_soc ?? null),
      akuTemp: b.bau?.tcell_avg ?? b.bau?.tcell_max ?? bpcs?.bms_max_temp,
      safetyOk: (bfault & 0x4440) === 0,
      alarmCount: bc,
    };
  });

  // Eelarvutused teise rea + detailipaneeli jaoks (kui aku olemas)
  let pBatt = null, soc = null, socClamped = 0, akuTemp = null;
  let safetyOk = true, alarmCount = 0, stateName = null, stateClass = null;
  let pcs = null;
  if (hasBattery) {
    pcs = pcsAvail ? park.pcs : null;
    // SOC eelistus: BAU (otsene BMS) > PCS bms_soc (PCS edasiserveeritud)
    soc = (park.bau?.soc != null) ? park.bau.soc : (pcs?.bms_soc ?? null);
    socClamped = Math.max(0, Math.min(100, soc || 0));
    // Aku võimsus = BAU energy meter (aku kapi AC sisend) → kW.
    // Positiivne = laeb, negatiivne = tühjeneb. Fallback PCS-le kui BAU pole online.
    pBatt = (park.bau?.meter_power_total != null)
      ? park.bau.meter_power_total / 1000
      : (pcs?.p_total ?? null);
    if (pcs) {
      [stateName, stateClass] = pcsStatusInfo(pcs.pcs_status);
    } else {
      // PCS kadunud — kuvame BAU run_state'i põhjal
      const rs = park.bau?.run_state;
      stateName = rs == null ? 'PCS offline' : `BAU run ${rs}`;
      stateClass = 'standby';
    }

    // Aku temp: eelista BAU otse (täpne), fallback PCS-kaudu
    akuTemp = park.bau?.tcell_avg ?? park.bau?.tcell_max ?? pcs?.bms_max_temp;

    // Ohutus — ainult KRIITILISED fault bitid (BAU.MD sec 9):
    //   bit6 = Emergency Stop Fault, bit10 = System Fault, bit14 = Chg/Dsg Circuit Fault
    // Mask 0x4440 = 0100 0100 0100 0000 (bittid 6, 10, 14)
    const fault = park.bau?.fault_state || 0;
    safetyOk = (fault & 0x4440) === 0;
    // Alarmid — Level 3 (kriitilised) bittide arv. Level 1/2 on info/hoiatused.
    const a3 = park.bau?.alarm_level3 || 0;
    const popcount = (n) => { let c = 0; while (n) { c += n & 1; n >>>= 1; } return c; };
    alarmCount = popcount(a3);

    batteryDetail = (
      <>
        {/* PCS — pinge muundamise süsteem (ainult kui PCS online) */}
        {pcs && (
          <div className="battery-block" style={{ gridColumn: '1 / -1' }}>
            <div className="battery-title">
              <span className="name">
                PCS
                <span className="host-tag">@ {park.pcs_host}</span>
                <span className="age-tag" style={ageStyle(pcs)}>{ageLabel(pcs)}</span>
              </span>
              <span className={`batt-stat ${stateClass}`}>
                <span className="dot" />
                {stateName}
              </span>
            </div>
            <div className="grid3">
              <div className="row"><span className="k">AC võimsus</span><span className="v">{fmt(pBatt, 2)} kW</span></div>
              <div className="row"><span className="k">DC võimsus</span><span className="v">{fmt(pcs.dc_power, 2)} kW</span></div>
              <div className="row"><span className="k">DC pinge</span><span className="v">{fmt(pcs.dc_voltage, 1)} V</span></div>
              <div className="row"><span className="k">DC vool</span><span className="v">{fmt(pcs.dc_current, 1)} A</span></div>
              <div className="row"><span className="k">Sagedus</span><span className="v">{fmt(pcs.hz, 2)} Hz</span></div>
              <div className="row"><span className="k">PF</span><span className="v">{fmt(pcs.pf, 3)}</span></div>
            </div>
          </div>
        )}
        {!pcs && park.pcs_host && !park.control?.pcs_polling_disabled && (
          <div className="battery-block" style={{ gridColumn: '1 / -1', opacity: 0.6 }}>
            <div className="battery-title">
              <span className="name">PCS<span className="host-tag">@ {park.pcs_host}</span></span>
              <span className="batt-stat standby"><span className="dot" />Offline</span>
            </div>
            <div style={{ padding: '8px 12px', fontSize: '0.85rem', color: 'var(--muted)' }}>
              PCS ei vasta — kuvame ainult BAU andmed allpool.
            </div>
          </div>
        )}
        {park.control?.pcs_polling_disabled && (
          <div className="battery-block" style={{ gridColumn: '1 / -1', background: '#FEF3C7', border: '1px solid #F59E0B', padding: '8px 12px', borderRadius: 6, fontSize: '0.78rem' }}>
            🛑 <strong>PCS Markedroidi all</strong> — EMS ei pollu ega juhi PCS-i. Aku SoC kuvame BAU otsest mõõtmist.
          </div>
        )}
        {park.control?.inverter_polling_disabled && (
          <div className="battery-block" style={{ gridColumn: '1 / -1', background: '#FEF3C7', border: '1px solid #F59E0B', padding: '8px 12px', borderRadius: 6, fontSize: '0.78rem' }}>
            🛑 <strong>Inverter Markedroidi all</strong> — EMS ei pollu inverteri telemetriat ega curtail-i.
          </div>
        )}
        {park.control?.markedroid_mqtt_host && (
          <div className="battery-block" style={{ gridColumn: '1 / -1', background: '#DBEAFE', border: '1px solid #3B82F6', padding: '8px 12px', borderRadius: 6, fontSize: '0.78rem' }}>
            🔗 <strong>Markedroid MQTT all</strong> @ {park.control.markedroid_mqtt_host} — grid + PV andmed reaalajas (battery + meter topic).
          </div>
        )}

        {/* BAU — Battery Active Unit (otse BMS-st @ bau_host:12230) */}
        {(park.bau_online && park.bau) && (
          <div className="battery-block" style={{ gridColumn: '1 / -1' }}>
            <div className="battery-title">
              <span className="name">
                BAU
                <span className="host-tag">@ {park.bau_host}</span>
                <span className="age-tag" style={ageStyle(park.bau)}>{ageLabel(park.bau)}</span>
              </span>
              <span className="batt-stat standby">
                {fmt(park.bau.soc, 1)}% · {fmt(park.bau.bank_voltage, 1)} V
              </span>
            </div>
            <div className="soc-bar">
              <div className="soc-bar-fill" style={{ width: `${Math.max(0, Math.min(100, park.bau.soc || 0))}%` }} />
            </div>
            <div className="grid3">
              <div className="row"><span className="k">Kogupinge</span><span className="v">{fmt(park.bau.bank_voltage, 1)} V</span></div>
              <div className="row"><span className="k">Kogu vool</span><span className="v">{fmt(park.bau.bank_current, 1)} A</span></div>
              <div className="row"><span className="k">SOC</span><span className="v">{fmt(park.bau.soc, 1)} %</span></div>
              <div className="row"><span className="k">SOH</span><span className="v">{fmt(park.bau.soh, 1)} %</span></div>
              <div className="row"><span className="k">Cell max V</span><span className="v">{fmt(park.bau.vcell_max / 1000, 3)} V</span></div>
              <div className="row"><span className="k">Cell min V</span><span className="v">{fmt(park.bau.vcell_min / 1000, 3)} V</span></div>
              <div className="row"><span className="k">Cell avg V</span><span className="v">{fmt(park.bau.vcell_avg / 1000, 3)} V</span></div>
              <div className="row"><span className="k">Cell max T</span><span className="v">{fmt(park.bau.tcell_max, 1)} °C</span></div>
              <div className="row"><span className="k">Cell min T</span><span className="v">{fmt(park.bau.tcell_min, 1)} °C</span></div>
              <div className="row"><span className="k">Cell avg T</span><span className="v">{fmt(park.bau.tcell_avg, 1)} °C</span></div>
              <div className="row"><span className="k">Lub. chg I</span><span className="v">{fmt(park.bau.current_chg_limit, 0)} A</span></div>
              <div className="row"><span className="k">Lub. dsg I</span><span className="v">{fmt(park.bau.current_dsg_limit, 0)} A</span></div>
              <div className="row"><span className="k">Chg saadav</span><span className="v">{fmt(park.bau.energy_chg_available, 0)} kWh</span></div>
              <div className="row"><span className="k">Dsg saadav</span><span className="v">{fmt(park.bau.energy_dsg_available, 0)} kWh</span></div>
              <div className="row"><span className="k">Insulation</span><span className="v">{fmt(park.bau.insulation_resistance, 0)} kΩ</span></div>
            </div>

            {/* Aktiivsed alarmid eestikeelseteks nimedega */}
            {Array.isArray(park.bau.active_alarms) && park.bau.active_alarms.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', fontWeight: 500, marginBottom: 4 }}>
                  Aktiivsed alarmid ({park.bau.active_alarms.length})
                </div>
                {park.bau.active_alarms.map((a, i) => {
                  const color = a.severity === 'critical' ? 'var(--negative)' : a.severity === 'warning' ? 'var(--warning)' : 'var(--info)';
                  return (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.66rem', color, padding: '2px 0' }}>
                      <span>{a.name}</span>
                      <span style={{ fontFamily: 'JetBrains Mono', fontSize: '0.58rem', opacity: 0.7 }}>
                        {a.source === 'fault' ? 'F' : `L${a.level}`}/{a.bit}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* BAU Energy Meter (aku kapi AC sisend) */}
        {(park.bau_online && park.bau && park.bau.meter_voltage_a !== null) && (
          <div className="battery-block" style={{ gridColumn: '1 / -1' }}>
            <div className="battery-title">
              <span className="name">BAU Energy Meter<span className="host-tag">PowerEnv @ {park.bau_host}</span></span>
              <span className="batt-stat standby">{fmt(park.bau.meter_power_total, 0)} W</span>
            </div>
            <div className="grid3">
              <div className="row"><span className="k">L1 pinge</span><span className="v">{fmt(park.bau.meter_voltage_a, 1)} V</span></div>
              <div className="row"><span className="k">L2 pinge</span><span className="v">{fmt(park.bau.meter_voltage_b, 1)} V</span></div>
              <div className="row"><span className="k">L3 pinge</span><span className="v">{fmt(park.bau.meter_voltage_c, 1)} V</span></div>
              <div className="row"><span className="k">L1 vool</span><span className="v">{fmt(park.bau.meter_current_a, 2)} A</span></div>
              <div className="row"><span className="k">L2 vool</span><span className="v">{fmt(park.bau.meter_current_b, 2)} A</span></div>
              <div className="row"><span className="k">L3 vool</span><span className="v">{fmt(park.bau.meter_current_c, 2)} A</span></div>
              <div className="row"><span className="k">Sagedus</span><span className="v">{fmt(park.bau.meter_frequency, 2)} Hz</span></div>
              <div className="row"><span className="k">PF</span><span className="v">{fmt(park.bau.meter_pf, 3)}</span></div>
              <div className="row"><span className="k">Reaktiiv</span><span className="v">{fmt(park.bau.meter_reactive_power, 3)} kvar</span></div>
              <div className="row"><span className="k">Energia kokku</span><span className="v">{fmt(park.bau.meter_energy_fwd, 0)} kWh</span></div>
              <div className="row"><span className="k">Sisetemp</span><span className="v">{fmt(park.bau.meter_temperature, 1)} °C</span></div>
              <div className="row"><span className="k">Niiskus</span><span className="v">{fmt(park.bau.meter_humidity, 1)} %RH</span></div>
            </div>
          </div>
        )}

        {/* Vedelikjahutus */}
        {(park.bau_online && park.bau) && (
          <div className="meter-block" style={{ gridColumn: '1 / -1' }}>
            <div className="meter-title">
              Vedelikjahutus
              <span className="host-tag" style={{
                marginLeft: 'auto', fontFamily: 'JetBrains Mono', fontSize: '0.62rem',
                color: park.bau.lc_fault_count > 0 ? 'var(--negative)' : 'var(--text-mute)',
                textTransform: 'none', letterSpacing: 0,
                fontWeight: park.bau.lc_fault_count > 0 ? 600 : 400,
              }}>
                {park.bau.lc_unit_state ? 'Sees' : 'Väljas'} · {park.bau.lc_fault_count > 0 ? `${park.bau.lc_fault_count} viga` : 'OK'}
                {park.bau.liquid_cooling_comm_fault ? ' · COMM FAULT' : ''}
              </span>
            </div>

            {/* Aktiivsete vigade nimekiri (kui on) */}
            {Array.isArray(park.bau.lc_active_faults) && park.bau.lc_active_faults.length > 0 && (
              <div style={{
                marginBottom: 8, padding: '6px 10px',
                background: 'rgba(220,38,38,0.06)',
                border: '1px solid rgba(220,38,38,0.18)',
                borderRadius: 3,
              }}>
                {park.bau.lc_active_faults.map((f, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.66rem', color: '#7f1d1d', padding: '1px 0' }}>
                    <span>{f.name}</span>
                    <span style={{ fontFamily: 'JetBrains Mono', fontSize: '0.6rem', opacity: 0.7 }}>{f.addr}={f.value}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="grid3">
              <div className="row"><span className="k">Pump</span><span className="v">{park.bau.lc_pump_state ? 'Sees' : 'Väljas'}</span></div>
              <div className="row"><span className="k">Pumba kiirus</span><span className="v">{fmt(park.bau.lc_pump_speed, 0)} %</span></div>
              <div className="row"><span className="k">Kompressor</span><span className="v">{park.bau.lc_compressor_state ? 'Sees' : 'Väljas'}</span></div>
              <div className="row"><span className="k">Kompr. kiirus</span><span className="v">{fmt(park.bau.lc_compressor_speed, 0)}</span></div>
              <div className="row"><span className="k">Vent. kiirus</span><span className="v">{fmt(park.bau.lc_external_fan_speed, 0)}</span></div>
              <div className="row"><span className="k">Elektriküte</span><span className="v">{park.bau.lc_electric_heating_state ? 'Sees' : 'Väljas'}</span></div>
              <div className="row"><span className="k">Effluent vesi</span><span className="v">{fmt(park.bau.lc_effluent_temp, 1)} °C</span></div>
              <div className="row"><span className="k">Backwater vesi</span><span className="v">{fmt(park.bau.lc_backwater_temp, 1)} °C</span></div>
              <div className="row"><span className="k">Exhaust</span><span className="v">{fmt(park.bau.lc_exhaust_temp, 1)} °C</span></div>
              <div className="row"><span className="k">Ümbritsev</span><span className="v">{fmt(park.bau.lc_ambient_temp, 1)} °C</span></div>
              <div className="row"><span className="k">Effluent rõhk</span><span className="v">{fmt(park.bau.lc_effluent_pressure, 2)} bar</span></div>
              <div className="row"><span className="k">Backwater rõhk</span><span className="v">{fmt(park.bau.lc_backwater_pressure, 2)} bar</span></div>
              <div className="row"><span className="k">Refrig setpoint</span><span className="v">{fmt(park.bau.lc_refrigeration_point, 1)} °C</span></div>
              <div className="row"><span className="k">Heat setpoint</span><span className="v">{fmt(park.bau.lc_heating_point, 1)} °C</span></div>
              <div className="row"><span className="k">Režiim</span><span className="v">{fmt(park.bau.lc_operational_mode, 0)}</span></div>
            </div>
          </div>
        )}

        {/* Sisekliima — niiskus, küte, BAU temp */}
        {(park.bau_online && park.bau) && (
          <div className="meter-block" style={{ gridColumn: '1 / -1' }}>
            <div className="meter-title">
              Sisekliima
              <span className="host-tag" style={{ marginLeft: 'auto', fontFamily: 'JetBrains Mono', fontSize: '0.62rem', color: 'var(--text-mute)', textTransform: 'none', letterSpacing: 0 }}>
                {fmt(park.bau.meter_temperature, 1)}°C · {fmt(park.bau.meter_humidity, 0)}%RH
                {park.bau.humTemp_comm_fault ? ' · ANDUR VIGA' : ''}
              </span>
            </div>
            <div className="grid3">
              <div className="row"><span className="k">Sisetemp</span><span className="v">{fmt(park.bau.meter_temperature, 1)} °C</span></div>
              <div className="row"><span className="k">Niiskus</span><span className="v">{fmt(park.bau.meter_humidity, 1)} %RH</span></div>
              <div className="row"><span className="k">BAU max temp</span><span className="v">{fmt(park.bau.bms_max_temp_env, 1)} °C</span></div>
              <div className="row"><span className="k">BAU min temp</span><span className="v">{fmt(park.bau.bms_min_temp_env, 1)} °C</span></div>
              <div className="row"><span className="k">Niiskus-eemaldaja</span><span className="v">{park.bau.dehum_state ? 'Sees' : 'Väljas'}</span></div>
              <div className="row"><span className="k">Niiskus auto/käsi</span><span className="v">{park.bau.dehum_auto_mode ? 'Auto' : 'Käsi'}</span></div>
              <div className="row"><span className="k">Niiskus start</span><span className="v">{fmt(park.bau.dehum_humidity_start, 0)} %</span></div>
              <div className="row"><span className="k">Niiskus stop</span><span className="v">{fmt(park.bau.dehum_humidity_stop, 0)} %</span></div>
              <div className="row"><span className="k">Elektriküte</span><span className="v">{park.bau.heat_state ? 'Sees' : 'Väljas'}</span></div>
              <div className="row"><span className="k">Küte start</span><span className="v">{fmt(park.bau.heat_start_value, 0)} °C</span></div>
            </div>
          </div>
        )}

        {/* Ohutus & sisendid */}
        {(park.bau_online && park.bau) && (
          <div className="meter-block" style={{ gridColumn: '1 / -1' }}>
            <div className="meter-title">
              Ohutus & sisendid
              <span className="host-tag" style={{
                marginLeft: 'auto',
                fontFamily: 'JetBrains Mono',
                fontSize: '0.62rem',
                color: park.bau.tempAndSmokeAlarm ? 'var(--negative)' : 'var(--positive)',
                textTransform: 'none',
                letterSpacing: 0,
                fontWeight: 600,
              }}>
                {park.bau.tempAndSmokeAlarm ? 'ALARM!' : 'OK'}
              </span>
            </div>
            <div className="grid3">
              <div className="row"><span className="k">Suitsu/temp alarm</span><span className="v">{park.bau.tempAndSmokeAlarm ? 'AKTIIVNE' : 'OK'}</span></div>
              <div className="row"><span className="k">DI 1H</span><span className="v">{park.bau.di1H ? 'Sees' : 'Väljas'}</span></div>
              <div className="row"><span className="k">DI 7L</span><span className="v">{park.bau.di7L ? 'Sees' : 'Väljas'}</span></div>
              <div className="row"><span className="k">DI 8L</span><span className="v">{park.bau.di8L ? 'Sees' : 'Väljas'}</span></div>
              <div className="row"><span className="k">DI 9L</span><span className="v">{park.bau.di9L ? 'Sees' : 'Väljas'}</span></div>
              <div className="row"><span className="k">DI 10L</span><span className="v">{park.bau.di10L ? 'Sees' : 'Väljas'}</span></div>
              <div className="row"><span className="k">DI 11L</span><span className="v">{park.bau.di11L ? 'Sees' : 'Väljas'}</span></div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Esimene rida = 4 cell kui hasBattery, muidu 3
  const row1Cols = hasBattery ? 4 : 3;

  // Deye hübriid: "Ohutus" (BAU fault-bitid) asemel inverteri olek; alarmid AlarmGeneratorist
  const isDeye = park.meter_protocol === 'deye_v5';
  const deyeState = { 0: 'Ootel', 1: 'Enesetest', 2: 'Normaalne', 3: 'Hoiatus', 4: 'Rike' }[park.deye?.state] || '—';
  const deyeOk = park.deye?.state === 2 && !park.deye?.fault;
  if (isDeye) alarmCount = (park._alarms || []).length;

  return (
    <div className={`park${hasBattery ? ' has-battery' : ''}${isMuted ? ' muted' : ''}`}>
      <div className="park-head">
        <h2>{park.name}</h2>
        <div className="meta">
          <span className="host">{park.host}</span>
          {isMuted ? (
            <span className="pill pill-mute" title={park.muted_reason || ''}>
              <span className="dot" />
              Vaigistatud
            </span>
          ) : (
            <span className="pill pill-on">
              <span className="dot" />
              Online
            </span>
          )}
        </div>
      </div>

      {isMuted && park.muted_reason && (
        <div style={{
          padding: '6px 12px',
          fontSize: '0.78rem',
          background: 'rgba(120, 120, 120, 0.08)',
          borderBottom: '1px solid var(--divider)',
          color: 'var(--text-2)',
        }}>
          Vaigistatud: {park.muted_reason}
        </div>
      )}

      {/* Rida 1: PEJ · AKU · Tarbimine · VÕRK */}
      <div className="flow" style={{ gridTemplateColumns: `repeat(${row1Cols}, 1fr)` }}>
        <div className="flow-cell solar">
          <div className="lbl">PEJ</div>
          <div className="val">{fmt(pSolar)}<span className="unit">kW</span></div>
        </div>

        {hasBattery && (
          <div className="flow-cell battery">
            <div className="lbl">AKU</div>
            <div className="val">{fmt(pBatt)}<span className="unit">kW</span></div>
          </div>
        )}

        <div className="flow-cell consumption">
          <div className="lbl">Tarbimine</div>
          <div className="val">{fmt(cons)}<span className="unit">kW</span></div>
        </div>

        <div className={`flow-cell ${exporting ? 'export' : 'import'}`}>
          <div className="lbl">VÕRK {exporting ? '↑' : '↓'}</div>
          <div className="val">{fmt(Math.abs(pGrid || 0))}<span className="unit">kW</span></div>
        </div>
      </div>

      {/* Rida 2: SOC · Aku temp · Ohutus · Alarmid (ainult akuga park'idel) */}
      {hasBattery && (
        <div className="flow" style={{ gridTemplateColumns: 'repeat(4, 1fr)', borderTop: '1px solid var(--divider)' }}>
          <div className="flow-cell battery">
            <div className="lbl">SOC</div>
            <div className="val">{fmt(soc, 1)}<span className="unit">%</span></div>
          </div>
          <div className="flow-cell">
            <div className="lbl">Aku temp</div>
            <div className="val">{fmt(akuTemp, 1)}<span className="unit">°C</span></div>
          </div>
          {isDeye ? (
            <div className="flow-cell">
              <div className="lbl">Inverter</div>
              <div className="val" style={{ color: deyeOk ? 'var(--positive)' : 'var(--warning)', fontSize: '0.95em' }}>
                {deyeState}
              </div>
            </div>
          ) : (
            <div className="flow-cell">
              <div className="lbl">Ohutus</div>
              <div className="val" style={{ color: safetyOk ? 'var(--positive)' : 'var(--negative)' }}>
                {safetyOk ? 'OK' : 'Viga'}
              </div>
            </div>
          )}
          <Link
            to={`/alarms#slot-${park.slot}`}
            className="flow-cell"
            title="Mine alarmide lehele"
            style={{
              textDecoration: 'none', color: 'inherit', cursor: 'pointer',
              transition: 'background 0.15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-2)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
          >
            <div className="lbl">Alarmid {alarmCount > 0 && '›'}</div>
            <div className="val" style={{ color: alarmCount === 0 ? 'var(--positive)' : 'var(--warning)' }}>
              {alarmCount === 0 ? 'OK' : alarmCount}
            </div>
          </Link>
        </div>
      )}

      {/* Rida 2b: iga täiendava aku (battery #2+) SOC/temp/ohutus/alarmid */}
      {extraBatteries.map((b) => (
        <div key={b.battery} className="flow" style={{ gridTemplateColumns: 'repeat(4, 1fr)', borderTop: '1px solid var(--divider)' }}>
          <div className="flow-cell battery">
            <div className="lbl">SOC #{b.battery}</div>
            <div className="val">{fmt(b.soc, 1)}<span className="unit">%</span></div>
          </div>
          <div className="flow-cell">
            <div className="lbl">Aku temp</div>
            <div className="val">{fmt(b.akuTemp, 1)}<span className="unit">°C</span></div>
          </div>
          <div className="flow-cell">
            <div className="lbl">Ohutus</div>
            <div className="val" style={{ color: b.safetyOk ? 'var(--positive)' : 'var(--negative)' }}>
              {b.safetyOk ? 'OK' : 'Viga'}
            </div>
          </div>
          <Link
            to={`/alarms#slot-${park.slot}`}
            className="flow-cell"
            title="Mine alarmide lehele"
            style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
          >
            <div className="lbl">Alarmid {b.alarmCount > 0 && '›'}</div>
            <div className="val" style={{ color: b.alarmCount === 0 ? 'var(--positive)' : 'var(--warning)' }}>
              {b.alarmCount === 0 ? 'OK' : b.alarmCount}
            </div>
          </Link>
        </div>
      ))}

      {/* Rida 3: Täna kWh näidikud (PEJ tootmine, AKU laadis/tühj, VÕRK eksport/import) */}
      {park.today && (
        <div className="flow" style={{ gridTemplateColumns: hasBattery ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)', borderTop: '1px solid var(--divider)' }}>
          <div className="flow-cell solar">
            <div className="lbl">PEJ täna</div>
            <div className="val">{fmt(park.today.solar_kwh, 1)}<span className="unit">kWh</span></div>
          </div>
          {hasBattery && (
            <div className="flow-cell solar">
              <div className="lbl">PEJ prognoos</div>
              <div className="val">
                {park.forecast_today_kwh != null ? fmt(park.forecast_today_kwh, 0) : '—'}<span className="unit">kWh</span>
              </div>
            </div>
          )}
          <div className="flow-cell export">
            <div className="lbl">Võrku täna</div>
            <div className="val">{fmt(park.today.grid_export_kwh, 1)}<span className="unit">kWh</span></div>
          </div>
          <div className="flow-cell import">
            <div className="lbl">Võrgust täna</div>
            <div className="val">{fmt(park.today.grid_import_kwh, 1)}<span className="unit">kWh</span></div>
          </div>
        </div>
      )}

      {/* Detailne info kolitud "Seadmed" lehele — kaart Töölaual jätab käreda ülevaate. */}
    </div>
  );
}

export default ParkCard;
